import Papa from 'papaparse';
import { z } from 'zod';
import type {
  ContactImportFieldApi,
  ContactImportPreviewDataApi,
} from '../types/contact-import-api.types';

export const CONTACT_IMPORT_MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

export const CONTACT_IMPORT_SAMPLE_CSV = [
  'email_address,first,last,phone_number,plan,signup_source',
  'maria.keller@example.ch,Maria,Keller,+41 79 555 0110,Pro,Referral',
  'sofia.rossi@example.it,Sofia,Rossi,+39 340 555 0121,Pro,Website',
  'noah.fischer@example.ch,Noah,Fischer,,Free,Event',
  'not-an-email,Jonas,Weber,+41 78 555 0199,Enterprise,Referral',
  'elin.andersson@example.se,Elin,Andersson,+46 70 555 0133,,Website',
  'lukas.meier@example.ch,Lukas,Meier,+41 76 555 0177,Enterprise,Referral',
].join('\n');

export type ContactImportMappingTarget = string;

export type ParsedContactCsv = {
  headers: string[];
  rows: Record<string, string>[];
};

export type ContactImportPreviewStatus = 'valid' | 'invalid';

export type ContactImportPreviewRow = {
  index: number;
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  locale: string;
  attributes: Record<string, string>;
  status: ContactImportPreviewStatus;
  reasons: string[];
  /** Set once the backend dry run has answered for this row: it matches an existing contact
   * (or an earlier row) and will update it rather than create a new one. */
  willUpdate?: boolean;
  /** The earlier row of this file with the same email or phone, when there is one. */
  duplicateOfRow?: number;
};

export type ContactImportPreviewCounts = {
  total: number;
  validCount: number;
  invalidCount: number;
  importableCount: number;
  willUpdateCount: number;
};

const emailSchema = z.string().trim().email();

const toOptionalString = (value: unknown): string => {
  if (value == null) return '';
  return String(value).trim();
};

export const parseContactCsvForImport = (csvText: string): ParsedContactCsv => {
  const parsed = Papa.parse<Record<string, unknown>>(csvText, {
    header: true,
    skipEmptyLines: 'greedy',
    delimiter: ',',
  });

  const fatalError = parsed.errors.find(
    (error) => error.type === 'Quotes' || error.type === 'Delimiter'
  );
  if (fatalError) {
    throw new Error(fatalError.message || 'Failed to parse CSV file');
  }

  const headers = (parsed.meta.fields ?? []).map((header) => String(header).trim()).filter(Boolean);
  const rows = parsed.data
    .map((row) => {
      const normalized: Record<string, string> = {};
      for (const [key, value] of Object.entries(row)) {
        normalized[key.trim()] = toOptionalString(value);
      }
      return normalized;
    })
    .filter((row) => Object.values(row).some((value) => value.length > 0));

  return { headers, rows };
};

export const invertContactImportMapping = (
  uiMapping: Record<string, string>
): Record<string, string> => {
  const inverted: Record<string, string> = {};
  for (const [source, target] of Object.entries(uiMapping)) {
    const t = target.trim();
    if (!t) continue;
    inverted[t] = source;
  }
  return inverted;
};

export const buildInitialContactImportMappingFromFields = (
  headers: string[],
  fields: ContactImportFieldApi[]
): Record<string, string> => {
  const used = new Set<string>();
  return Object.fromEntries(
    headers.map((header) => {
      const normalized = header.trim().toLowerCase();
      const match = fields.find((field) => {
        if (used.has(field.field)) return false;
        const fieldKey = field.field.toLowerCase();
        const label = field.label.toLowerCase();
        const bareAttr = field.field.startsWith('attributes.')
          ? field.field.slice('attributes.'.length).toLowerCase()
          : '';
        return (
          normalized === fieldKey ||
          normalized === label ||
          normalized.includes(label) ||
          (bareAttr && normalized.includes(bareAttr)) ||
          (fieldKey === 'email' && normalized.includes('email')) ||
          (fieldKey === 'phone' &&
            (normalized.includes('phone') || normalized.includes('mobile'))) ||
          (fieldKey === 'firstname' && normalized.includes('first')) ||
          (fieldKey === 'lastname' && normalized.includes('last'))
        );
      });
      if (match) used.add(match.field);
      return [header, match?.field ?? ''];
    })
  );
};

export type ValidateContactImportMappingResult = { ok: true } | { ok: false; error: string };

export const validateContactImportMapping = (
  mapping: Record<string, ContactImportMappingTarget>
): ValidateContactImportMappingResult => {
  const targets = Object.values(mapping)
    .map((target) => target.trim())
    .filter(Boolean);
  const hasContactPoint = targets.includes('email') || targets.includes('phone');
  const duplicateTarget = targets.find((target, index) => targets.indexOf(target) !== index);

  if (duplicateTarget) {
    return { ok: false, error: 'Two columns cannot map to the same field.' };
  }

  if (!hasContactPoint) {
    return { ok: false, error: 'Map at least one column to Email or Phone.' };
  }

  return { ok: true };
};

export const buildContactImportPreviewRows = (params: {
  rows: Record<string, string>[];
  mapping: Record<string, ContactImportMappingTarget>;
  /** Required custom attributes; rows missing a value are flagged like the server will. */
  requiredAttributes?: { key: string; label: string }[];
}): ContactImportPreviewRow[] => {
  const { rows, mapping, requiredAttributes = [] } = params;

  return rows.map((raw, rowIndex) => {
    const record = {
      email: '',
      phone: '',
      firstName: '',
      lastName: '',
      locale: '',
      attributes: {} as Record<string, string>,
    };

    for (const [header, target] of Object.entries(mapping)) {
      const trimmedTarget = target.trim();
      if (!trimmedTarget) continue;
      const value = raw[header] ?? '';
      if (trimmedTarget.startsWith('attributes.')) {
        record.attributes[trimmedTarget.slice('attributes.'.length)] = value;
      } else if (
        trimmedTarget === 'email' ||
        trimmedTarget === 'phone' ||
        trimmedTarget === 'firstName' ||
        trimmedTarget === 'lastName' ||
        trimmedTarget === 'locale'
      ) {
        record[trimmedTarget] = value;
      }
    }

    const reasons: string[] = [];
    if (!record.email && !record.phone) {
      reasons.push('No email or phone');
    }
    if (record.email && !emailSchema.safeParse(record.email).success) {
      reasons.push('Invalid email format');
    }
    for (const required of requiredAttributes) {
      if (!record.attributes[required.key]?.trim()) {
        reasons.push(`${required.label} is required`);
      }
    }

    return {
      index: rowIndex + 1,
      email: record.email,
      phone: record.phone,
      firstName: record.firstName,
      lastName: record.lastName,
      locale: record.locale,
      attributes: record.attributes,
      status: reasons.length > 0 ? 'invalid' : 'valid',
      reasons,
    };
  });
};

export const summarizeContactImportPreview = (
  previewRows: ContactImportPreviewRow[]
): ContactImportPreviewCounts => {
  let validCount = 0;
  let invalidCount = 0;
  let willUpdateCount = 0;

  for (const row of previewRows) {
    if (row.status === 'invalid') invalidCount += 1;
    else validCount += 1;
    if (row.willUpdate) willUpdateCount += 1;
  }

  return {
    total: previewRows.length,
    validCount,
    invalidCount,
    importableCount: validCount,
    willUpdateCount,
  };
};

/**
 * The local rows with the backend dry run's verdicts laid over them, matched by 1-based row
 * number. The server applies the worker's exact rules (types, required attributes, duplicates),
 * so its answer replaces the local check for every row it examined; rows past its limit keep the
 * local check.
 */
export const applyContactImportServerPreview = (
  localRows: ContactImportPreviewRow[],
  preview: Pick<ContactImportPreviewDataApi, 'rows'>
): ContactImportPreviewRow[] => {
  const byRowNumber = new Map(preview.rows.map((row) => [row.rowNumber, row]));

  return localRows.map((row) => {
    const server = byRowNumber.get(row.index);
    if (!server) return row;

    const invalid = server.outcome === 'invalid';
    return {
      ...row,
      status: invalid ? 'invalid' : 'valid',
      reasons: invalid && server.reason?.trim() ? [server.reason.trim()] : [],
      willUpdate: server.outcome === 'update',
      duplicateOfRow: server.duplicateOfRow ?? undefined,
    };
  });
};
